/**
 * Normalization utilities for environmental and non-energy charges
 *
 * Handles the mismatch between DB snake_case keys and UI camelCase/Title Case keys:
 *   DB: { lrec: { year_1: { rate_per_kwh, power_percentage, price_per_certificate } } }
 *   UI: { LREC: { year_1: { ratePerKwh, powerPercentage, pricePerCertificate } } }
 *
 * Also applies the RATE/KWH derivation formula when ratePerKwh is missing:
 *   ratePerKwh = (pricePerCertificate * powerPercentage) / 1000
 * When powerPercentage is also missing, uses caller-supplied sys_config defaults.
 */

import type { EnvironmentalCharges, NonEnergyCharges } from '../types/offerTypes';
import { ENV_SCHEMES, ENV_ALIAS_TO_DISPLAY_KEY, resolvePowerPct, extractCalendarYear, type PowerEnvYearConfig } from './envSchemeRegistry';
import { roundEnvRate } from './ratePrecision';
import { sequenceEnvCharges } from './envYearSequence';

// ============================================================================
// Environmental Charges Normalization
// ============================================================================

/**
 * Maps DB snake_case env charge keys to UI uppercase keys.
 * Derived from envSchemeRegistry.ts — add new schemes there, not here.
 */
const ENV_KEY_MAP: Record<string, string> = ENV_ALIAS_TO_DISPLAY_KEY;

/** Maps DB snake_case field names to UI camelCase field names */
const ENV_FIELD_MAP: Record<string, string> = {
  'rate_per_kwh': 'ratePerKwh',
  'power_percentage': 'powerPercentage',
  'price_per_certificate': 'pricePerCertificate',
};

/** @deprecated Use PowerEnvYearConfig from envSchemeRegistry.ts (year-wise) instead. Kept only
 * so any lingering external import doesn't hard-crash; no longer used within this file. */
export type PowerPctDefaults = {
  LREC: number;
  SREC: number;
  'VEEC/ESS/REES': number;
  ESC?: number;
  PRC?: number;
  [key: string]: number | undefined;
};

type EnvYearData = {
  ratePerKwh?: number | null;
  powerPercentage?: number | null;
  pricePerCertificate?: number | null;
  // Other fields (date_range/dateRange, etc.) are preserved verbatim by normalizeEnvYearData
  // below (not stripped) purely so resolvePowerPct's year-wise lookup can extract a calendar
  // year from this same object later — not otherwise read/used by this module.
  [key: string]: unknown;
};

/**
 * Normalize a single year's environmental charge data from snake_case to camelCase. Unknown
 * fields (e.g. date_range) are preserved as-is alongside the 3 normalized ones, so the calendar
 * year can still be extracted from this object later for year-wise power% default resolution.
 */
const normalizeEnvYearData = (data: Record<string, unknown>): EnvYearData => {
  const result: EnvYearData = { ...data };

  Object.entries(data).forEach(([key, value]) => {
    const normalizedField = ENV_FIELD_MAP[key] || key;
    if (normalizedField === 'ratePerKwh') result.ratePerKwh = value as number | null;
    else if (normalizedField === 'powerPercentage') result.powerPercentage = value as number | null;
    else if (normalizedField === 'pricePerCertificate') result.pricePerCertificate = value as number | null;
  });

  return result;
};

/**
 * Apply rate derivation formula to a single year's data.
 * If ratePerKwh is missing/zero but pricePerCertificate is present:
 *   ratePerKwh = (pricePerCertificate * powerPercentage) / 1000
 * When powerPercentage is also missing, uses the scheme's year-wise default from sys_config.
 */
const applyRateDerivation = (
  yearData: EnvYearData,
  defaultPowerPct: number
): EnvYearData => {
  const rate = yearData.ratePerKwh ?? 0;
  const cert = yearData.pricePerCertificate ?? 0;
  const docPct = yearData.powerPercentage ?? 0;
  const effectivePct = docPct > 0 ? docPct : defaultPowerPct;

  // Rate already provided — still fill in power% from defaults if missing
  if (rate > 0) {
    if (docPct <= 0 && effectivePct > 0) {
      return { ...yearData, powerPercentage: effectivePct };
    }
    return yearData;
  }

  // Need cert price to derive rate
  if (cert <= 0) {
    if (docPct <= 0 && effectivePct > 0) {
      return { ...yearData, powerPercentage: effectivePct };
    }
    return yearData;
  }

  if (effectivePct <= 0) return yearData;

  return {
    ...yearData,
    // Rounded only to strip float noise (0.12498789776700002 -> 0.1249878978), NOT to
    // shorten the value. Environmental rates land around 0.0067 c/kWh, so the old 4dp
    // rounding left just two significant figures and moved every environmental line by
    // up to ~0.7%. See ratePrecision.ts.
    ratePerKwh: roundEnvRate((cert * effectivePct) / 1000),
    powerPercentage: effectivePct,
  };
};

/**
 * Normalize environmental charges from DB format (snake_case) to UI format (camelCase uppercase keys).
 * Also derives missing ratePerKwh values using the formula: (cert × pct) / 1000.
 *
 * @param charges      Raw env charges in any supported format
 * @param defaults     Optional year-wise fallback power percentages from sys_config.powerEnvironment
 *                     (displayKey -> year -> %). Used when both ratePerKwh AND powerPercentage
 *                     are missing from the document, resolved per-year via each year's own
 *                     date_range calendar year (not a single flat value for the whole scheme).
 */
export const normalizeEnvironmentalCharges = (
  charges: Record<string, unknown> | undefined,
  defaults?: PowerEnvYearConfig
): EnvironmentalCharges | undefined => {
  if (!charges) return undefined;

  const keys = Object.keys(charges);

  // Helper: derive rates for a whole scheme object
  const deriveScheme = (
    schemeData: Record<string, EnvYearData>,
    schemeKey: string
  ): Record<string, EnvYearData> => {
    if (!defaults) return schemeData;
    const result: Record<string, EnvYearData> = {};
    Object.entries(schemeData).forEach(([yearKey, yearData]) => {
      // schemeData: see extractCalendarYear — a cumulative schedule is resolved by end date.
      const defaultPct = resolvePowerPct(defaults, schemeKey, extractCalendarYear(yearData, schemeData));
      result[yearKey] = applyRateDerivation(yearData, defaultPct);
    });
    return result;
  };

  // ── Flat format from parsedOffer.ts: { year_1: { lrec_rate, sres_rate, veec_rate, esc_rate, reps_rate, ... }, ... } ──
  // Flat field name for scheme 'veec' is still `veec_rate` (not `veec/ess/rees_rate`) — use scheme.key, not displayKey.
  const year1 = charges.year_1 as Record<string, unknown> | undefined;
  const isFlatFormat = !!year1 && typeof year1 === 'object' &&
    ENV_SCHEMES.some(s => `${s.key}_rate` in year1! || `${s.key}_cert_price` in year1!);

  if (isFlatFormat) {
    const bySchemeKey: Record<string, Record<string, EnvYearData>> = {};
    ENV_SCHEMES.forEach(s => { bySchemeKey[s.key] = {}; });

    keys.filter(k => k.startsWith('year_')).forEach(yearKey => {
      const yr = charges[yearKey] as Record<string, unknown>;
      if (!yr || typeof yr !== 'object') return;
      const sharedPriceCert = (yr.price_per_certificate as number | null) ?? null;
      const sharedPowerPct  = (yr.power_percentage as number | null) ?? null;

      ENV_SCHEMES.forEach(s => {
        const rate = (yr[`${s.key}_rate`] as number | null) ?? null;
        const cert = (yr[`${s.key}_cert_price`] as number | null) ?? sharedPriceCert;
        // Skip schemes with no data at all for this year (avoid seeding empty rows for every scheme)
        if (rate === null && cert === null) return;
        bySchemeKey[s.key][yearKey] = { ratePerKwh: rate, pricePerCertificate: cert, powerPercentage: sharedPowerPct };
      });
    });

    const result: Record<string, Record<string, EnvYearData>> = {};
    ENV_SCHEMES.forEach(s => {
      if (Object.keys(bySchemeKey[s.key]).length > 0) {
        result[s.displayKey] = deriveScheme(bySchemeKey[s.key], s.displayKey);
      }
    });
    // Always guarantee LREC/SREC/VEEC keys exist (backward compat with consumers expecting them)
    ['LREC', 'SREC', 'VEEC/ESS/REES'].forEach(dk => {
      if (!result[dk]) result[dk] = {};
    });
    // Periods made consecutive before anything consumes them — see envYearSequence.
    return sequenceEnvCharges(result) as unknown as EnvironmentalCharges;
  }

  // ── Already normalized (uppercase keys: LREC, SREC, VEEC/ESS/REES) ──
  const isAlreadyNormalized = keys.some(k => k === 'LREC' || k === 'SREC' || k === 'VEEC/ESS/REES');

  if (isAlreadyNormalized) {
    const result: Record<string, Record<string, EnvYearData>> = {};

    keys.forEach(key => {
      const normalizedKey = ENV_KEY_MAP[key.toLowerCase()] || key;
      const yearData = charges[key] as Record<string, unknown>;
      if (!yearData || typeof yearData !== 'object') return;

      const schemeYears: Record<string, EnvYearData> = {};
      Object.entries(yearData).forEach(([yearKey, yearValue]) => {
        if (yearKey.startsWith('year_') && yearValue && typeof yearValue === 'object') {
          schemeYears[yearKey] = normalizeEnvYearData(yearValue as Record<string, unknown>);
        }
      });
      result[normalizedKey] = deriveScheme(schemeYears, normalizedKey);
    });

    // Periods made consecutive before anything consumes them — see envYearSequence.
    return sequenceEnvCharges(result) as unknown as EnvironmentalCharges;
  }

  // ── Snake_case keys (lrec, sres, veec) ──
  const result: Record<string, Record<string, EnvYearData>> = {};

  Object.entries(charges).forEach(([key, value]) => {
    const normalizedKey = ENV_KEY_MAP[key.toLowerCase()];
    if (!normalizedKey) return;

    const yearData = value as Record<string, unknown>;
    if (!yearData || typeof yearData !== 'object') return;

    const schemeYears: Record<string, EnvYearData> = {};
    Object.entries(yearData).forEach(([yearKey, yearValue]) => {
      if (yearKey.startsWith('year_') && yearValue && typeof yearValue === 'object') {
        schemeYears[yearKey] = normalizeEnvYearData(yearValue as Record<string, unknown>);
      }
    });
    result[normalizedKey] = deriveScheme(schemeYears, normalizedKey);
  });

  // Periods made consecutive before anything consumes them — see envYearSequence.
  return sequenceEnvCharges(result) as unknown as EnvironmentalCharges;
};

// ============================================================================
// Non-Energy Charges Normalization
// ============================================================================

/** Maps DB snake_case non-energy charge keys to UI Title Case */
export const NON_ENERGY_KEY_MAP: Record<string, string> = {
  'meter_agreement': 'Meter Agreement',
  'supplementary_metering': 'Supplementary Metering',
  'service_charge': 'Service Charge',
  'market_fees': 'Market Fees',
};

/**
 * Normalize non-energy charges from snake_case to Title Case
 * Deduplicates keys that map to the same normalized key
 */
export const normalizeNonEnergyCharges = (charges: NonEnergyCharges): NonEnergyCharges => {
  const normalized: NonEnergyCharges = {};

  Object.entries(charges).forEach(([key, value]) => {
    const normalizedKey = NON_ENERGY_KEY_MAP[key.toLowerCase()] || NON_ENERGY_KEY_MAP[key] || key;
    // Prefer non-zero values when deduplicating
    if (normalized[normalizedKey] !== undefined && normalized[normalizedKey] !== 0) return;
    normalized[normalizedKey] = value;
  });

  return normalized;
};

/**
 * Format a non-energy charge key for display (snake_case → Title Case)
 */
export const formatNonEnergyLabel = (key: string): string => {
  if (NON_ENERGY_KEY_MAP[key.toLowerCase()]) return NON_ENERGY_KEY_MAP[key.toLowerCase()];
  return key.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
};
